import styled from 'styled-components';
import { BarTone } from '../../constants';

export const DAY_COLUMN_WIDTH = 34;
export const TRACK_LABEL_WIDTH = 190;
/**
 * 레인 높이 — 2행 막대(제목 + 메타)를 여백까지 담는다.
 *
 * 격자가 맞아야 하므로 **레인 높이는 균일**하다. 1행으로 접힌 짧은 막대는 이 안에서
 * 세로 가운데 정렬되고, 남는 공간은 비워 둔다 — 레인마다 높이를 다르게 하면
 * 주말 띠·격자선 배경과 어긋난다.
 *
 * 값 근거는 설계 문서(`design/amondz-promo-gantt-bar-redesign-20260915.html` §13 조절기)의
 * "중간" 프리셋: 상하 7 + 제목행간 16 + 메타행간 14 + 행간격 4 + 테두리 2 = 50.
 */
export const BAR_HEIGHT = 50;
const BAR_GAP = 6;

/** 펼친 쿠폰 한 줄 (line-height 14 + 항목 간격 3) */
export const COUPON_ROW_HEIGHT = 17;

/**
 * 펼침 목록이 막대에 더하는 고정 높이.
 *
 * margin-top 3 + padding-top 5 + 구분선 1 + **부모 flex gap 4** = 13.
 * ⚠️ 부모 gap 을 빼먹으면 전 구간에서 2px 씩 모자라 `overflow: hidden` 에 잘린다 —
 * flex 자식이 함께 압축되면서 **제목까지 사라진다.** 1px 여유를 더해 14 로 둔다.
 */
export const COUPON_LIST_PADDING = 14;

/**
 * 메타 줄 폭 예산 — 무엇을 얼마나 담을지 계산하는 근사값들.
 *
 * 정확한 텍스트 폭은 렌더 전에 알 수 없으므로 **보수적으로 잡는다.** 모자라게 잡으면
 * 넘쳐서 잘리고(칩이 '#' 한 글자만 남는다), 넉넉히 잡으면 들어갈 것도 생략될 뿐이라
 * 후자가 안전하다.
 */
/** 막대 좌우 패딩 + 스트라이프 + 여유 */
export const BAR_META_RESERVED = 42;
/** 기획전 배지 — 아이콘 + #id 까지 */
/**
 * 시각 라벨(14:00~18:00)이 차지하는 폭.
 *
 * 타임딜처럼 시간 단위로 진행하는 항목에만 붙는다. **값 자체가 정보라** 쿠폰 칩보다
 * 먼저 예산을 받는다 — 칩과 같은 순위로 두면 쿠폰이 하나만 붙어도 4일 막대에서 시간이 사라진다.
 */
export const TIME_BADGE_WIDTH = 68;

export const EVENT_BADGE_WIDTH = 58;
/** 기획전명을 함께 보여줄 때 추가로 드는 폭 */
export const EVENT_NAME_WIDTH = 70;
/** 쿠폰 칩 `#5329 48%` + 간격 */
export const CHIP_WIDTH_WITH_ID = 70;
/** 쿠폰 칩 `48%` 만 + 간격 */
export const CHIP_WIDTH_PLAIN = 40;
/** 출처·유형 평문 한 덩어리 */
export const PLAIN_META_WIDTH = 46;

/**
 * 2행을 유지할 최소 폭 — **4일(136px)부터 2행**.
 *
 * 실측(dev 421건) 분포로 정했다. 3일 이하가 28% 인데 그 폭(≤102px)에 2행을 넣으면
 * 제목조차 못 읽는다. 반대로 180px 로 잡으면 **4·5일 막대 10건이 1행이 되어
 * 쿠폰·기획전 연결이 통째로 가려진다** — 골드데이(4일)가 그 사례였다.
 *
 * 1행으로 접힌 막대의 메타는 hover 카드가 전담한다.
 */
export const BAR_TWO_ROW_MIN_WIDTH = 120;

export const StyledGanttWrap = styled.div`
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  overflow-x: auto;
`;

/** 트랙 라벨 열 + 일자 그리드가 같은 가로 스크롤을 공유하도록 하나의 min-width 를 준다. */
export const StyledGanttInner = styled.div<{ $totalDays: number }>`
  min-width: ${({ $totalDays }) => TRACK_LABEL_WIDTH + $totalDays * DAY_COLUMN_WIDTH}px;
`;

export const StyledRow = styled.div`
  display: flex;
  align-items: stretch;
`;

/**
 * 트랙 라벨 열. 가로 스크롤 시에도 보이도록 sticky 고정 —
 * 2개월 조회 시 60열이 넘어 라벨이 없으면 어느 밴드인지 알 수 없다.
 */
export const StyledTrackLabel = styled.div<{ $color?: string }>`
  flex: 0 0 ${TRACK_LABEL_WIDTH}px;
  /* sticky 가 containing block 이 되므로 접기 버튼은 이 안에서 절대 배치된다 */
  position: sticky;
  left: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  /* 왼쪽은 접기 버튼(16px) 자리를 비워 둔다 — 겹치면 트랙명이 가려진다 */
  padding: 8px 12px 8px 30px;
  background-color: #fafafa;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #f3f4f6;
  border-left: ${({ $color }) => ($color ? `3px solid ${$color}` : 'none')};

  .track-name {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
  }

  /* 시트 라벨에 박혀 있던 운영 규칙(주 N개 이상)을 부제로 승계 */
  .track-rule {
    font-size: 11px;
    color: #9ca3af;
  }

  /* 미달 주 요약 — 띠는 해당 주 구간에만 보이므로 라벨에서 전체를 알 수 있게 한다 */
  .track-shortage {
    display: inline-block;
    margin-top: 4px;
    padding: 1px 6px;
    border-radius: 10px;
    background-color: rgba(239, 68, 68, 0.12);
    color: #dc2626;
    font-size: 10px;
    font-weight: 700;
    cursor: help;
  }
`;

export const StyledHeaderCell = styled.div`
  flex: 0 0 ${TRACK_LABEL_WIDTH}px;
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: #f3f4f6;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
`;

/**
 * ⚠️ **스크롤에 따라 변하는 값은 props 로 받지 않는다** — 호출부에서 인라인 `style` 로 넘긴다.
 *
 * styled-components 는 props 보간이 있으면 값이 바뀔 때마다 새 클래스를 만들고 CSS 를 삽입한다.
 * 가상화는 스크롤마다 열 수·오프셋을 바꾸므로 props 로 두면 스크롤 한 번에 수백 개 규칙이
 * 재생성돼 화면이 멈춘다(실측: 초기 로딩은 빨라졌는데 스크롤이 얼어붙었다).
 * 인라인 style 은 클래스를 만들지 않고 해당 요소의 style 속성만 바꾼다.
 */
export const StyledGrid = styled.div`
  flex: 1;
  display: grid;
`;

/** 주차 헤더 — 한 주가 7열을 병합한다 (구글시트의 주차 행과 동일한 형태). */
export const StyledWeekHeader = styled.div`
  padding: 6px 4px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  color: #4b5563;
  background-color: #f3f4f6;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  overflow: hidden;
`;

/**
 * 일자 헤더 — 날짜 + 요일 2줄.
 * 색상은 `DAY_TYPE_COLOR`(토=파랑 / 일·공휴일=빨강) 를 그대로 받는다. 오늘은 노란 배경이 우선.
 */
export const StyledDayHeader = styled.div<{ $color: string; $background: string; $isToday: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 3px 0;
  font-size: 10px;
  line-height: 1.2;
  text-align: center;
  border-right: 1px solid #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  background-color: ${({ $background, $isToday }) => ($isToday ? '#fef3c7' : $background || '#fff')};
  color: ${({ $color }) => $color};

  .day-number {
    font-weight: ${({ $isToday }) => ($isToday ? 700 : 500)};
  }

  .day-weekday {
    font-size: 9px;
    opacity: 0.85;
  }
`;

/** 항목 막대가 놓이는 밴드. 행 수만큼 높이가 늘어난다. */
/**
 * 토·일 세로 띠를 **배경 그라디언트로** 그린다.
 *
 * 주가 월요일 시작이라 토(6번째)·일(7번째) 열이 **7일 주기로 규칙적**이다.
 * 노드로 그리면 연 단위 조회에서 106개 × 트랙 수 = 600개가 넘고, 그 삽입이
 * React 커밋의 `insertBefore` 비용으로 그대로 나타난다(프로파일 실측: 스크립트 시간의 90% 이상).
 * 배경은 노드가 0개다.
 *
 * ⚠️ 공휴일은 날짜가 불규칙해 이 방식으로 못 그린다 → `StyledDayStripe` 로 개별 렌더한다.
 * 공휴일이 토·일과 겹치면 노드가 배경 위에 얹혀 빨강이 우선한다(기존 규칙 유지).
 */
const SATURDAY_STRIPE = 'rgba(37,99,235,0.05)';
const SUNDAY_STRIPE = 'rgba(220,38,38,0.05)';

const weekendStripes = `
  repeating-linear-gradient(
    to right,
    transparent 0,
    transparent ${DAY_COLUMN_WIDTH * 5}px,
    ${SATURDAY_STRIPE} ${DAY_COLUMN_WIDTH * 5}px,
    ${SATURDAY_STRIPE} ${DAY_COLUMN_WIDTH * 6}px,
    ${SUNDAY_STRIPE} ${DAY_COLUMN_WIDTH * 6}px,
    ${SUNDAY_STRIPE} ${DAY_COLUMN_WIDTH * 7}px
  )
`;

const dayGridLines = `
  repeating-linear-gradient(
    to right,
    #f3f4f6 0,
    #f3f4f6 1px,
    transparent 1px,
    transparent ${DAY_COLUMN_WIDTH}px
  )
`;

/**
 * 트랙 밴드 그리드.
 *
 * ⚠️ 열 정의·오프셋·배경 원점은 **스크롤마다 바뀌므로 인라인 `style`** 로 받는다(위 StyledGrid 주석 참고).
 * 배경(격자선·주말 띠)은 요소 왼쪽 기준으로 반복되므로, 가상화로 앞쪽 열을 안 그려 요소가 밀리면
 * **밀린 만큼 배경 원점도 되돌려야** 토·일 위치가 유지된다 — 그 계산도 호출부에서 한다.
 */
/**
 * 트랙 접기 버튼.
 *
 * 트랙이 6개라 스크롤이 길어지는데, 지금 보는 트랙만 남기고 접으면 비교가 쉬워진다.
 * 라벨 영역 왼쪽 여백에 겹쳐 둬서 트랙명이 밀리지 않는다.
 */
export const StyledTrackToggle = styled.button<{ $collapsed: boolean }>`
  position: absolute;
  left: 8px;
  top: 50%;
  margin-top: -8px;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #9ca3af;
  font-size: 9px;
  line-height: 1;
  cursor: pointer;
  transition: transform 0.12s ease;
  transform: rotate(${({ $collapsed }) => ($collapsed ? '-90deg' : '0deg')});

  &:hover {
    background-color: #eef0f3;
    color: #4b5563;
  }
`;

/** 접힌 트랙의 요약 줄 — 몇 건이 숨어 있는지 알려준다 */
export const StyledCollapsedBand = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 7px 14px;
  font-size: 11px;
  color: #9ca3af;
  background-color: #fcfcfd;
  border-bottom: 1px solid #f3f4f6;
`;

export const StyledBandGrid = styled.div<{ $rowHeights: number[] }>`
  flex: 1;
  position: relative;
  display: grid;
  /**
   * 행 높이를 **레인별로** 받는다.
   *
   * 쿠폰을 펼친 막대가 있는 레인만 그만큼 키운다 — 겹쳐 띄우면 아래 막대를 가리고
   * 왼쪽 트랙 라벨 영역까지 삐져나간다(overflow 를 풀어야 하므로 스크롤 컨테이너를 넘어선다).
   * 세로는 가상화 대상이 아니라 행 높이를 바꿔도 열 계산에 영향이 없다.
   */
  grid-template-rows: ${({ $rowHeights }) => $rowHeights.map((h) => `${h}px`).join(' ')};
  row-gap: ${BAR_GAP}px;
  padding: ${BAR_GAP}px 0;
  /**
   * ⚠️ **밴드 구분선을 여기에 그리지 않는다.**
   *
   * border 로 그리면 그 1px 에 배경이 안 칠해지고, 배경 레이어로 올려도 **띠 위를 가로질러**
   * 결국 주말·공휴일 띠가 밴드마다 끊겨 보인다. 세로로 이어지는 띠가 날짜를 읽는 축이라
   * 그쪽을 살린다. 밴드 구분은 왼쪽 트랙 라벨의 경계선과 배경색 차이가 맡는다.
   */
  background-image: ${dayGridLines}, ${weekendStripes};
`;

/**
 * 오늘 열 강조.
 *
 * 헤더 셀만 노랗게 칠하면 **세로로 내려오면서 어느 열이 오늘인지 잃어버린다** —
 * 2개월 조회 시 60열이 넘어 위쪽 헤더를 다시 올려다봐야 한다.
 * 간트의 today-line 관례대로 열 전체에 옅은 띠와 좌우 경계선을 둔다.
 */
export const StyledTodayStripe = styled.div`
  pointer-events: none;
  margin: -${BAR_GAP}px 0;
  background-color: rgba(250, 204, 21, 0.14);
  box-shadow:
    inset 1px 0 0 rgba(202, 138, 4, 0.45),
    inset -1px 0 0 rgba(202, 138, 4, 0.45);
`;

/** 공휴일 세로 띠. 막대 뒤에 깔린다 (토·일은 위 배경이 담당). */
export const StyledDayStripe = styled.div<{ $stripe: string }>`
  /**
   * ⚠️ **아래 주말 띠를 덮어야 한다.**
   *
   * 공휴일 띠는 반투명이라 그대로 얹으면 토·일 띠와 **섞여 보라빛이 된다**
   * (2026-09-26 추석이 토요일과 겹치는 경우). 헤더는 "공휴일 우선" 으로 빨강인데
   * 배경만 다른 색이면 같은 날이 두 규칙으로 읽힌다.
   * 흰색을 깔아 아래를 지우고 그 위에 공휴일 색을 얹는다.
   */
  background-color: #fff;
  background-image: linear-gradient(${({ $stripe }) => `${$stripe}, ${$stripe}`});
  pointer-events: none;
  /**
   * 밴드의 상하 padding(BAR_GAP)까지 덮는다.
   *
   * 공휴일 띠는 주말 띠(background-image)와 달리 **grid item** 이라 padding 영역에 닿지 않는다.
   * 그대로 두면 주말은 이어지는데 공휴일만 밴드마다 끊긴다.
   * 정확히 padding 만큼만 넓혀 **이웃 밴드로 넘치지는 않게** 한다.
   */
  margin: -${BAR_GAP}px 0;
`;

/**
 * 주당 최소 개수 미달 주 — 트랙 밴드의 그 주 구간(7열)에 **대각선 패턴 + 상단 라인**으로 표시한다.
 *
 * 표시 방식이 두 번 바뀐 자리다. 판단 근거를 남겨둔다:
 * - **단색 배경은 안 된다.** 계획이 얇은 구간(시트에 아직 안 적힌 하반기 등)에서는 조회한 모든 주가
 *   미달이라 밴드가 통째로 붉어져, 요일 띠(토·일)와 섞이고 "어느 주가 문제인가"가 사라진다.
 * - **상단 라인만으로도 부족하다.** 첫 행에 막대가 있으면 그 뒤로 가려서 구간의 시작·끝이 안 읽힌다.
 * - 그래서 **대각선 패턴**을 쓴다. 단색이 아니라 요일 띠와 구분되고, 막대 사이 빈 공간에서 패턴이
 *   드러나 구간이 묶여 보인다. 라인은 막대가 없는 행에서 경계를 또렷하게 하는 보조 역할.
 *
 * 막대 **아래**에 깔린다(z-index 없음 + 먼저 렌더) — 막대를 가리면 항목 정보가 묻히고 클릭·드래그도 막힌다.
 */
export const StyledShortageBand = styled.div`
  background-image: repeating-linear-gradient(45deg, rgba(239, 68, 68, 0.13) 0 5px, transparent 5px 11px);
  border-top: 3px solid rgba(239, 68, 68, 0.55);
  cursor: help;
`;
/**
 * 항목 막대 — 시트처럼 2줄로 정보를 담는다.
 *   1줄: 쿠폰/취합 방식/유형 배지
 *   2줄: 담당자 · 제목
 * 좁은 막대(`$isCompact`)는 배지 줄을 접고 제목만 한 줄로 보여준다.
 */
/**
 * 간트 막대.
 *
 * ⚠️ **div 다.** 안에 쿠폰 토글 버튼과 상세 링크(a)가 들어가는데,
 * HTML 은 button 안에 interactive content 를 허용하지 않는다 — 중첩하면 클릭이
 * 엉뚱하게 동작한다(토글이 눌러도 접히지 않는 증상). role·tabIndex·키보드 처리를
 * 직접 얹어 버튼과 같은 접근성을 유지한다.
 */
export const StyledBar = styled.div<{
  $tone: BarTone;
  $opacity: number;
  $isCompact: boolean;
  $isDraggable: boolean;
  $isPreview: boolean;
}>`
  /* grid-column·grid-row 는 스크롤마다 바뀌므로 인라인 style 로 받는다 */
  /**
   * 펼친 막대를 위로 올리지 않는다.
   *
   * 겹쳐 띄우던 시절에는 아래 막대를 덮어야 해서 z-index 를 올렸는데, 지금은 레인 높이가
   * 늘어나 겹칠 일이 없다. 그대로 두면 sticky 트랙 라벨(z-index 2)까지 덮어 가린다.
   */
  z-index: ${({ $isPreview }) => ($isPreview ? 3 : 1)};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: ${({ $isCompact }) => ($isCompact ? '10px' : '4px')};
  min-width: 0;
  margin: 0 1px;
  /* 좌측은 스트라이프 5px 를 넘겨 실제 여백이 13px 이 되게 한다 */
  padding: 7px 13px 7px 18px;
  border: 1px solid ${({ $tone }) => $tone.border};
  border-radius: 6px;
  background-color: ${({ $tone }) => $tone.bg};
  opacity: ${({ $opacity }) => $opacity};
  color: ${({ $tone }) => $tone.text};
  text-align: left;
  cursor: ${({ $isDraggable }) => ($isDraggable ? 'grab' : 'pointer')};
  /**
   * 넘치는 내용은 자른다.
   *
   * 펼친 막대의 쿠폰 목록은 레인 높이(rowHeights)가 미리 늘어나 담기므로 여기서 잘리지 않는다.
   * 잘리는 것은 폭이 모자랄 때의 메타 줄인데, 그건 GanttView 의 폭 예산이 애초에 들어갈
   * 만큼만 그려 막는다. 이 선언은 그래도 새는 경우의 마지막 방어다.
   */
  overflow: hidden;
  /* 드래그 중 텍스트 선택·터치 스크롤이 끼어들지 않게 */
  touch-action: none;
  user-select: none;
  /**
   * 높이는 **항상 레인을 꽉 채운다.**
   *
   * 폭에 따라 높이까지 달리했더니 같은 트랙 안에서 막대 높이가 들쭉날쭉해 보였다 —
   * 게다가 "높이를 정하는 조건(폭)" 과 "메타를 그릴지 정하는 조건(메타 유무)" 이 달라서,
   * 폭은 넓지만 메타가 없는 막대가 **1줄짜리 내용에 2줄 높이**로 남는 일이 생겼다.
   * 높이는 고정하고, 좁을 때 **내용만** 줄인다.
   */
  height: 100%;
  /**
   * 펼칠 때 **아래로만** 자라야 한다.
   *
   * 'stretch' 로 두면 grid 가 아이템을 셀 높이에 맞추려 해서, 내용이 셀보다 커지는 순간
   * 위아래로 함께 번진다 — 위로 넘친 부분은 주차 헤더에 가려 잘린다.
   * 'start' 로 셀 상단에 고정하면 넘치는 만큼은 전부 아래로 간다.
   */
  align-self: start;
  /* 이동 중인 막대는 그림자로 띄워 어느 것을 옮기는지 드러낸다 */
  /* 펼침은 레인 안에서 커지므로 '떠 있다' 는 표현이 필요 없다 — 그림자는 드래그 중에만 */
  box-shadow: ${({ $isPreview }) => ($isPreview ? '0 2px 8px rgba(0,0,0,0.22)' : 'none')};
  outline: ${({ $isPreview, $tone }) => ($isPreview ? `2px solid ${$tone.stripe}` : 'none')};

  /**
   * 좌측 스트라이프 — 트랙을 구분하는 **유일한 색면**이다.
   *
   * 막대 전체를 원색으로 칠하지 않는 이유는 두 가지다. 수십 개가 깔릴 때 고채도 면적이
   * 화면을 덮어 눈이 피로하고, 원색 위 흰 글씨는 대비가 WCAG AA 에 못 미친다.
   */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    border-radius: 5px 0 0 5px;
    background-color: ${({ $tone }) => $tone.stripe};
  }

  &:hover {
    filter: brightness(0.985);
    border-color: ${({ $tone }) => $tone.stripe};
  }

  &:active {
    cursor: ${({ $isDraggable }) => ($isDraggable ? 'grabbing' : 'pointer')};
  }

  /* 좌우 끝 리사이즈 핸들 — hover 시에만 드러내 평소 시각 노이즈를 줄인다 */
  .resize-handle {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 6px;
    cursor: col-resize;
    background-color: transparent;
    z-index: 2;
  }

  &:hover .resize-handle {
    background-color: ${({ $tone }) => $tone.stripe};
    opacity: 0.35;
  }

  .resize-handle-start {
    left: 0;
    border-radius: 5px 0 0 5px;
  }

  .resize-handle-end {
    right: 0;
    border-radius: 0 5px 5px 0;
  }

  /* ── 1행: 제목·담당자 ── */
  .bar-main {
    /* 공간이 모자라도 줄어들지 않는다 — 압축되면 글자가 통째로 사라진다 */
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    white-space: nowrap;
    overflow: hidden;
  }

  .bar-manager {
    flex: 0 0 auto;
    opacity: 0.6;
    font-weight: 500;
    font-size: 11px;
  }

  .bar-title {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── 2행: 메타 ── */
  .bar-meta {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 10px;
    line-height: 14px;
    white-space: nowrap;
    overflow: hidden;
  }

  /**
   * 참고 정보(출처·유형·시각)는 **배경 없는 회색 평문**이다.
   *
   * 배지를 쓰면 중요해 보인다 — 기표 규칙상 배지는 "값"(쿠폰 혜택)에만 쓰고,
   * 있다/없다는 형태(아이콘)로, 참고는 평문으로 나눈다. 그래야 배지가 늘어나도 위계가 유지된다.
   */
  .bar-plain {
    flex: 0 0 auto;
    opacity: 0.62;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* 시각 — 출처·유형(bar-plain)보다 진하게. 타임딜에서는 이게 핵심 정보다 */
  .bar-time {
    flex: 0 0 auto;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    opacity: 0.82;
  }

  /* 기획전 연결 — 형태(아이콘)로 훑고, id 로 대조한다. 연결이 없으면 아예 없다 */
  .bar-event {
    flex: 0 1 auto;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 0 8px;
    border-radius: 4px;
    background-color: #ccfbf1;
    color: #0f766e;
    font-size: 9px;
    font-weight: 700;
    line-height: 14px;
    text-decoration: none;
  }

  .bar-event:hover {
    background-color: #99f6e4;
    text-decoration: underline;
  }

  .bar-event .anticon {
    font-size: 9px;
    flex: 0 0 auto;
  }

  /* 기획전명 — id 만으로는 어느 기획전인지 알 수 없다. 폭이 부족하면 말줄임 */
  .bar-event .ev-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
  }

  /**
   * 1행으로 접힌 막대의 최소 표식.
   *
   * 메타 줄이 없어 연결 유무조차 안 보이는 상태를 면하게만 한다 —
   * 제목을 밀어내지 않도록 아주 작게 두고, 자세한 내용은 hover 카드가 전담한다.
   */
  .bar-flag {
    flex: 0 0 auto;
    margin-left: -2px;
  }

  .bar-flag-event {
    font-size: 9px;
    color: #0f766e;
  }

  .bar-flag-coupon {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #6d28d9;
  }

  /* ── 쿠폰 칩 ── */
  .bar-coupons {
    flex: 0 1 auto;
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    cursor: pointer;
    overflow: hidden;
  }

  /**
   * 쿠폰 칩 — '#id' + 혜택.
   *
   * 혜택만 보여주면 '10원 10원' 처럼 **어떤 쿠폰인지 구분되지 않는다.**
   * 기획전이 '#4855' 로 id 를 주는 것과도 어긋나 통일성이 깨진다.
   * id 는 작고 흐리게, 혜택은 굵게 — 훑을 때는 혜택이, 대조할 때는 id 가 읽히게 한다.
   */
  .bar-chip {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 0 8px;
    border-radius: 4px;
    background-color: #ede9fe;
    color: #6d28d9;
    font-size: 9px;
    font-weight: 800;
    line-height: 14px;
  }

  .bar-chip em {
    font-style: normal;
    font-weight: 600;
    opacity: 0.62;
  }

  /* 계획 단계(memo 파싱)는 점선 — 아직 실제 쿠폰이 연결되지 않았다는 뜻이다 */
  .bar-chip-planned {
    background-color: transparent;
    border: 1px dashed currentColor;
    color: inherit;
    opacity: 0.6;
    font-weight: 700;
  }

  .bar-chip-more {
    flex: 0 0 auto;
    font-size: 9px;
    opacity: 0.55;
  }

  /* ── 펼친 쿠폰 목록 ── */
  .bar-coupon-list {
    flex: 0 0 auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin-top: 3px;
    padding-top: 5px;
    border-top: 1px dashed ${({ $tone }) => $tone.border};
  }

  /**
   * 펼친 쿠폰 한 줄.
   *
   * ⚠️ **한 줄을 넘기면 안 된다.** 레인 높이를 '줄 수 × 17px' 로 미리 계산해 두기 때문에,
   * 이름이 길어 줄바꿈되면 그만큼 막대가 넘쳐 잘린다 — 제목까지 밀려 사라진다.
   */
  .bar-coupon-item {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    height: 14px;
    font-size: 10px;
    line-height: 14px;
    white-space: nowrap;
  }

  /**
   * 쿠폰 상세 링크 — **기획전(청록)과 다른 색**이어야 한다.
   * 같은 색이면 막대 안에서 둘을 구분할 수 없다. 쿠폰 칩과 같은 보라 계열로 묶는다.
   */
  .bar-coupon-link {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    padding: 0 6px;
    border-radius: 4px;
    background-color: #ede9fe;
    color: #6d28d9;
    font-size: 9px;
    font-weight: 700;
    line-height: 14px;
    text-decoration: none;
  }

  .bar-coupon-link:hover {
    background-color: #ddd6fe;
    text-decoration: underline;
  }

  .bar-coupon-item .amt {
    flex: 0 0 auto;
    min-width: 42px;
    padding: 0 5px;
    border-radius: 3px;
    background-color: #ede9fe;
    color: #6d28d9;
    font-weight: 800;
    text-align: center;
  }

  .bar-coupon-item .nm {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    opacity: 0.82;
  }

  .bar-coupon-item .pd {
    flex: 0 0 auto;
    margin-left: auto;
    opacity: 0.5;
    font-size: 9px;
  }
`;

/**
 * 항목이 없는 트랙에 겹쳐 두는 안내.
 *
 * 밴드 자체는 그리드를 그대로 쓴다 — 빈 밴드로 갈아치우면 격자선·주말 띠·공휴일 띠가
 * 그 트랙에서만 끊겨 날짜 축이 깨져 보인다. 문구만 위에 얹는다.
 */
export const StyledBandPlaceholder = styled.div`
  grid-column: 1 / -1;
  grid-row: 1 / span 1;
  display: flex;
  align-items: center;
  padding-left: 14px;
  font-size: 12px;
  color: #d1d5db;
  pointer-events: none;
`;

/**
 * 막대·경고 라인이 공유하는 hover 카드. antd 'Tooltip' 을 항목마다 두는 대신 하나만 둔다.
 * 'position: fixed' 라서 기준 요소의 'getBoundingClientRect()' 값을 그대로 쓸 수 있다.
 */
export const StyledHoverCard = styled.div`
  position: fixed;
  z-index: 1100;
  max-width: 320px;
  padding: 8px 10px;
  border-radius: 6px;
  background-color: rgba(17, 24, 39, 0.94);
  color: #fff;
  font-size: 12px;
  line-height: 1.5;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
  /* 카드가 마우스를 받으면 막대에서 커서가 벗어난 것으로 처리돼 깜빡인다 */
  pointer-events: none;
  white-space: pre-line;
`;

export const StyledEmpty = styled.div`
  padding: 60px 0;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
`;
